<template>
  <OnboardingLayout
    :show-sign-in="false"
    no-top-padding
  >
    <div class="success">
      <GenericAnimation
        :animation-data="animationData"
        loop
        autoplay
        :width="xs ? 120 : 400"
        :speed="0.5"
      />

      <div class="success__copy">
        <h1 class="text-h4 font-weight-medium">
          {{ $t('signup.v2.success.title-1') }}<br />{{ $t('signup.v2.success.title-2') }}
        </h1>
        <p class="text-body-1">
          {{ $t('signup.v2.success.text', { name: firstname }) }}
        </p>
      </div>

      <div
        v-if="xs"
        class="success__actions"
      >
        <v-btn
          color="primary"
          height="44"
          rounded="xl"
          block
          @click="goToOnboarding"
        >
          {{ $t('signup.v2.success.continue') }}
        </v-btn>
        <p class="success__hint">{{ $t('signup.v2.success.hint') }}</p>
      </div>
    </div>
  </OnboardingLayout>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'

import AnimationData from '@/assets/animations/rocket.json'
import { AuthInstance } from '@/auth'
import GenericAnimation from '@/components/GenericAnimation.vue'

import OnboardingLayout from './components/OnboardingLayout.vue'
import { useBetaOnboarding } from './composables/useBetaOnboarding.ts'

defineOptions({ name: 'BetaSignupSuccess' })
const { xs } = useDisplay()
const animationData = ref(AnimationData)
const REDIRECT_DELAY_MS = 4000
const router = useRouter()
const onboarding = useBetaOnboarding()
const $auth = inject<AuthInstance>('auth')
const firstname = computed(() => $auth.user?.name || '')
let timer: ReturnType<typeof setTimeout> | undefined

function goToOnboarding() {
  clearTimeout(timer)
  onboarding.reset()
  router.replace('/onboarding')
}

onMounted(() => {
  timer = setTimeout(goToOnboarding, REDIRECT_DELAY_MS)
})

onBeforeUnmount(() => clearTimeout(timer))
</script>

<style scoped>
.success {
  width: 450px;
  max-width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.success__copy {
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;
  color: rgb(var(--v-theme-on-surface));
}

.success__actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.success__hint {
  font-size: 11px;
  line-height: 15px;
  text-align: center;
  color: rgb(var(--v-theme-grey));
}

@media (max-width: 599px) {
  .success {
    width: 100%;
    padding: 24px 12px;
  }
  .success__copy h1 {
    font-size: 22px !important;
    line-height: 28px !important;
  }
  .success__copy p {
    font-size: 13px;
    line-height: 18px;
  }
}
</style>
